import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import AuthLayout from '../../components/layouts/AuthLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Mail, Lock, User, Building, Phone, AlertCircle, CheckCircle, MapPin, FileText, Wrench } from 'lucide-react';

const RegisterPage = () => {
  const { register } = useAuth();
  const { t, locale } = useI18n();
  const navigate = useNavigate();

  const TUNING_TOOL_OPTIONS = [
    'Autotuner', 'KESS', 'KTAG', 'MMFlex', 'Magic Motorsport',
    'Bitbox', 'CMD', 'Dimsport', 'Trasdata', 'Alientech', 'PCMFlash',
  ];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    phone: '',
    address_street: '',
    address_zip: '',
    address_city: '',
    address_country: 'DE',
    vat_id: '',
    tuning_tools: [],
    tuning_tools_other: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleTuningTool = (tool) => {
    setFormData(prev => ({
      ...prev,
      tuning_tools: prev.tuning_tools.includes(tool)
        ? prev.tuning_tools.filter(t => t !== tool)
        : [...prev.tuning_tools, tool],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (formData.password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        company: formData.company || null,
        phone: formData.phone || null,
        address_street: formData.address_street || null,
        address_zip: formData.address_zip || null,
        address_city: formData.address_city || null,
        address_country: formData.address_country || 'DE',
        vat_id: formData.vat_id || null,
        tuning_tools: formData.tuning_tools.length ? formData.tuning_tools : null,
        tuning_tools_other: formData.tuning_tools_other || null,
        locale: locale,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = [
    { met: formData.password.length >= 8, text: 'Mindestens 8 Zeichen' },
    { met: /[A-Z]/.test(formData.password), text: 'Ein Großbuchstabe' },
    { met: /[0-9]/.test(formData.password), text: 'Eine Zahl' },
  ];

  return (
    <AuthLayout>
      <div className="glass-card p-8" data-testid="register-form">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-2xl font-bold text-white mb-2">{t('auth.register')}</h1>
          <p className="text-slate-400 text-sm">Erstellen Sie Ihr Dealer-Konto</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="label-text">{t('auth.name')} *</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Max Mustermann"
                className="input-field pl-10"
                required
                data-testid="register-name"
              />
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="label-text">{t('auth.email')} *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="name@example.de"
                className="input-field pl-10"
                required
                data-testid="register-email"
              />
            </div>
          </div>

          {/* Company & Phone */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company" className="label-text">{t('auth.company')}</Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Firma GmbH"
                  className="input-field pl-10"
                  data-testid="register-company"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="label-text">{t('auth.phone')}</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+49 170..."
                  className="input-field pl-10"
                  data-testid="register-phone"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="pt-2">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> Rechnungsadresse
            </h3>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="address_street" className="label-text">Straße + Hausnr.</Label>
                <Input id="address_street" name="address_street" value={formData.address_street} onChange={handleChange} placeholder="Musterstraße 12" className="input-field" data-testid="register-street" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="address_zip" className="label-text">PLZ</Label>
                  <Input id="address_zip" name="address_zip" value={formData.address_zip} onChange={handleChange} placeholder="12345" className="input-field" data-testid="register-zip" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="address_city" className="label-text">Stadt</Label>
                  <Input id="address_city" name="address_city" value={formData.address_city} onChange={handleChange} placeholder="Berlin" className="input-field" data-testid="register-city" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-text">Land</Label>
                <Select value={formData.address_country} onValueChange={(value) => setFormData(prev => ({ ...prev, address_country: value }))}>
                  <SelectTrigger className="input-field" data-testid="register-country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#23272F] border-white/10">
                    <SelectItem value="DE">Deutschland</SelectItem>
                    <SelectItem value="AT">Österreich</SelectItem>
                    <SelectItem value="CH">Schweiz</SelectItem>
                    <SelectItem value="NL">Niederlande</SelectItem>
                    <SelectItem value="BE">Belgien</SelectItem>
                    <SelectItem value="LU">Luxemburg</SelectItem>
                    <SelectItem value="FR">Frankreich</SelectItem>
                    <SelectItem value="IT">Italien</SelectItem>
                    <SelectItem value="ES">Spanien</SelectItem>
                    <SelectItem value="PL">Polen</SelectItem>
                    <SelectItem value="CZ">Tschechien</SelectItem>
                    <SelectItem value="DK">Dänemark</SelectItem>
                    <SelectItem value="SE">Schweden</SelectItem>
                    <SelectItem value="GB">Großbritannien</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vat_id" className="label-text">USt-IdNr. <span className="text-slate-500 text-xs">(optional, EU-Kunden mit USt-ID erhalten Reverse-Charge)</span></Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input id="vat_id" name="vat_id" value={formData.vat_id} onChange={handleChange} placeholder="DE123456789" className="input-field pl-10" data-testid="register-vat" />
                </div>
              </div>
            </div>
          </div>

          {/* Tuning Tools */}
          <div className="pt-2">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
              <Wrench className="w-3.5 h-3.5" /> Tuning-Tools (Mehrfachauswahl)
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {TUNING_TOOL_OPTIONS.map(tool => (
                <button
                  key={tool}
                  type="button"
                  onClick={() => toggleTuningTool(tool)}
                  className={`text-xs px-3 py-2 rounded-lg border transition-all ${formData.tuning_tools.includes(tool) ? 'bg-[#EC2229]/10 border-[#EC2229]/40 text-white' : 'bg-white/[0.03] border-white/[0.06] text-slate-400 hover:text-white'}`}
                  data-testid={`register-tool-${tool.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {tool}
                </button>
              ))}
            </div>
            <Input
              name="tuning_tools_other"
              value={formData.tuning_tools_other}
              onChange={handleChange}
              placeholder="Andere Tools (Freitext)..."
              className="input-field mt-3"
              data-testid="register-tools-other"
            />
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password" className="label-text">{t('auth.password')} *</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="input-field pl-10"
                required
                data-testid="register-password"
              />
            </div>
            {/* Password Requirements */}
            {formData.password && (
              <div className="mt-2 space-y-1">
                {passwordRequirements.map((req, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    {req.met ? (
                      <CheckCircle className="w-3 h-3 text-emerald-500" />
                    ) : (
                      <div className="w-3 h-3 rounded-full border border-slate-600" />
                    )}
                    <span className={req.met ? 'text-emerald-500' : 'text-slate-500'}>{req.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="label-text">{t('auth.confirmPassword')} *</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                className="input-field pl-10"
                required
                data-testid="register-confirm-password"
              />
            </div>
          </div>

          {/* Terms */}
          <div className="flex items-start gap-2 text-xs text-slate-400">
            <input 
              type="checkbox" 
              required 
              className="mt-1 rounded border-white/20 bg-black/20" 
              data-testid="register-terms"
            />
            <span>
              Ich akzeptiere die{' '}
              <Link to="/agb" className="text-[#EC2229] hover:underline">AGB</Link>
              {' '}und die{' '}
              <Link to="/datenschutz" className="text-[#EC2229] hover:underline">Datenschutzerklärung</Link>
            </span>
          </div>

          <Button 
            type="submit" 
            className="btn-primary w-full"
            disabled={loading}
            data-testid="register-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              t('auth.registerButton')
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-slate-400">
            {t('auth.hasAccount')}{' '}
            <Link 
              to="/login" 
              className="text-[#EC2229] font-medium hover:underline"
              data-testid="login-link"
            >
              {t('auth.login')}
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default RegisterPage;
